
import { Card, CardAbility, AbilityTrigger } from './data/cards';
import type { TribeId } from './data/cards/types';

// A runtime instance of a card, which is guaranteed to have a unique ID.
// For now, it has the same shape as the base Card type.
export type CardInstance = Card;

/**
 * A union type representing the possible outcomes of a triggered ability.
 */
export type AbilityResult =
  | { type: "buff"; targetIndex: number; atkDelta: number; hpDelta: number }
  | { type: "buff_shop"; atkDelta: number; hpDelta: number } // Buffs a random shop unit
  | { type: "combat_buff"; targetIndex: number; atkDelta: number; hpDelta: number }
  | { type: "summon"; position: number; cardId: string }
  /** Snowball primitive: +1/+1 permanent per tag, plus a plaqueStacks bump
      that finishers (La Leyenda Tatuada, Mito del Barrio) cash in later. */
  | { type: "plaque_tag"; targetIndex: number; count: number }
  | { type: "gain_gold"; amount: number }
  | { type: "add_card"; tier: number } // Adds a random shop-pool card of that tier to hand
  /** Opens the Discover modal: three shop-pool cards to pick one from.
      `tribe` narrows the pool to a single sub-tribe (Reclutador de Cuadra);
      omit it for a plain tier discover (Traficante de Mercado). Only
      meaningful in the tavern — combat drops it. */
  | { type: "discover"; tier: number; tribe?: TribeId }
  | { type: "heal_hero"; amount: number }
  /** Stat change applied to every unit on the OPPOSING board for this
      combat. Deltas are usually negative (El Surfer's -1 Attack). */
  | { type: "enemy_debuff"; atkDelta: number; hpDelta: number }
  /** Straight damage to a specific enemy index (Botas de Casquillo's
      splash onto the unit behind the one it just hit). */
  | { type: "damage_enemy"; targetIndex: number; amount: number }
  /** Reorder two friendly slots (Bailarín de Respaldo). */
  | { type: "swap_positions"; a: number; b: number }
  /** Take gold off the player this ability's owner is fighting. */
  | { type: "drain_opponent_gold"; amount: number }
  | { type: "log"; message: string };

/**
 * Contextual information provided when an ability is triggered.
 */
export interface TriggerContext {
  owner: "player" | "enemy";
  /** Damage just taken/dealt — read by on_take_damage / on_deal_damage. */
  damageAmount?: number;
  /** True on the unit's first hit of the combat (on_take_damage). */
  isFirstTime?: boolean;
  /**
   * The opposing board, when the caller has one. Only combat supplies it;
   * tavern triggers leave it undefined, and abilities that need it simply
   * produce nothing there.
   */
  enemyBoard?: CardInstance[];
  /** Index of the enemy this unit is attacking (on_deal_damage). */
  enemyTargetIndex?: number;
  /**
   * Which slot on THIS board is doing the attacking. on_first_attack fires
   * on every slot, so self-scoped handlers compare it against sourceIndex
   * while bystander handlers use it as their target.
   */
  attackerIndex?: number;
  /** Units bought / played so far this tavern turn. */
  buysThisTurn?: number;
  playsThisTurn?: number;
  /** Current round, for "every Nth turn" effects. */
  roundNumber?: number;
  /** The current shop, for tavern triggers that read it. */
  shop?: CardInstance[];
  /**
   * Whether the player's hero power grants stats to units. Lets a card
   * key off the hero without importing the hero singleton into this
   * otherwise-pure module.
   */
  heroPowerBuffsUnits?: boolean;
}

/**
 * Extra stats owed to a unit whose Attack/Health just got swapped
 * (Chavo de la Máquina: "Whenever its stats are swapped, gain +1 Health").
 *
 * Only the ability-engine swap paths call this — the Bajitos tribe hop and
 * the hero-power swaps live in their own modules and are not covered.
 */
function swapReactions(
  board: CardInstance[],
  targetIndex: number,
): AbilityResult[] {
  const unit = board[targetIndex];
  if (!unit?.abilities) return [];
  return unit.abilities
    .filter((a) => a.id === 'bonus_on_stats_swapped')
    .map((a) => ({
      type: 'combat_buff' as const,
      targetIndex,
      atkDelta: a.params?.attack ?? 0,
      hpDelta: a.params?.health ?? 0,
    }))
    .filter((r) => r.atkDelta !== 0 || r.hpDelta !== 0);
}

/**
 * Processes a card's abilities for a specific trigger and returns the results.
 * @param trigger - The event that occurred (e.g., "lastbreath").
 * @param sourceIndex - The index of the card whose abilities are being triggered.
 * @param board - The current state of the board where the card is.
 * @param context - Additional contextual information.
 * @returns An array of AbilityResult objects.
 */
export function triggerAbilities(
  trigger: AbilityTrigger,
  sourceIndex: number,
  board: CardInstance[],
  context: TriggerContext
): AbilityResult[] {
  const sourceCard = board[sourceIndex];
  if (!sourceCard || !sourceCard.abilities) {
    return []; // No card or no abilities to trigger.
  }

  const results: AbilityResult[] = [];

  // Find all abilities on the card that match the current trigger.
  const triggeredAbilities = sourceCard.abilities.filter(
    (ability) => ability.trigger === trigger
  );

  for (const ability of triggeredAbilities) {
    // A simple, data-driven switch to handle different ability IDs.
    switch (ability.id) {
      case 'extra_gold_on_sell': {
        const amount = (ability.params && ability.params.amount != null) ? ability.params.amount : 0;
        if (amount > 0) {
          results.push({ type: 'gain_gold', amount });
        }
        break;
      }

      case 'buff_self_for_combat': {
        const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
        const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
        if (atk > 0 || hp > 0) {
          results.push({ type: 'buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
        }
        break;
      }

      case 'buff_random_friendly_for_combat': {
        // Find all valid targets (any other card on the board).
        let validTargets = board
          .map((card, index) => ({ card, index }))
          .filter(item => item.card && item.index !== sourceIndex);
        
        if (validTargets.length > 0) {
          const target = validTargets[Math.floor(Math.random() * validTargets.length)];
          const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
          const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
          if (atk > 0 || hp > 0) {
            results.push({ type: 'combat_buff', targetIndex: target.index, atkDelta: atk, hpDelta: hp });
          }
        }
        break;
      }
      case 'buff_random_friendly': {
        // Find all valid targets (any other card on the board).
        let validTargets = board
          .map((card, index) => ({ card, index }))
          .filter(item => item.card && item.index !== sourceIndex);
        
        // For lastbreaths, ensure the target is not also dying.
        if (trigger === 'lastbreath') {
            validTargets = validTargets.filter(item => item.card.health > 0);
        }

        if (validTargets.length > 0) {
          const target = validTargets[Math.floor(Math.random() * validTargets.length)];
          const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
          const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
          if (atk > 0 || hp > 0) {
            results.push({ type: 'buff', targetIndex: target.index, atkDelta: atk, hpDelta: hp });
          }
        }
        break;
      }

      case 'summon_token': {
        const tokenId = ability.params ? ability.params.tokenId : undefined;
        if (tokenId) {
          // Summon the token at the position of the source card (e.g., for lastbreaths).
          results.push({ type: 'summon', position: sourceIndex, cardId: tokenId });
        }
        break;
      }

      case 'buff_friendly_permanent': {
        // Summon: Give a friendly unit +X/+X permanently
        let validTargets = board
          .map((card, index) => ({ card, index }))
          .filter(item => item.card && item.index !== sourceIndex);

        if (validTargets.length > 0) {
          const target = validTargets[Math.floor(Math.random() * validTargets.length)];
          const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
          const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
          if (atk > 0 || hp > 0) {
            results.push({ type: 'buff', targetIndex: target.index, atkDelta: atk, hpDelta: hp });
          }
        }
        break;
      }

      case 'buff_leftmost_unit_combat': {
        // Summon: Give leftmost unit +X health/attack this combat
        if (board.length > 0) {
          const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
          const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
          if (atk > 0 || hp > 0) {
            results.push({ type: 'buff', targetIndex: 0, atkDelta: atk, hpDelta: hp });
          }
        }
        break;
      }

      case 'heal_hero_on_death': {
        // Last Breath: Heal your hero for X
        const healAmount = (ability.params && ability.params.amount != null) ? ability.params.amount : 0;
        if (healAmount > 0) {
          results.push({ type: 'heal_hero', amount: healAmount });
        }
        break;
      }

      case 'heal_hero_battlecry': {
        // Summon: Heal your hero for X when played
        const healAmount = (ability.params && ability.params.amount != null) ? ability.params.amount : 0;
        if (healAmount > 0) {
          results.push({ type: 'heal_hero', amount: healAmount });
        }
        break;
      }

      case 'heal_hero_summon': {
        // Summon: Heal your hero for X
        const healAmount = (ability.params && ability.params.amount != null) ? ability.params.amount : 0;
        if (healAmount > 0) {
          results.push({ type: 'heal_hero', amount: healAmount });
        }
        break;
      }

      case 'buff_random_end_of_turn': {
        // End of turn: Give a random friendly unit +X/+Y
        let validTargets = board
          .map((card, index) => ({ card, index }))
          .filter(item => item.card && item.index !== sourceIndex);

        if (validTargets.length > 0) {
          const target = validTargets[Math.floor(Math.random() * validTargets.length)];
          const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
          const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
          if (atk > 0 || hp > 0) {
            results.push({ type: 'buff', targetIndex: target.index, atkDelta: atk, hpDelta: hp });
          }
        }
        break;
      }

      case 'buff_self_start_of_combat': {
        // Start of combat: Gain +X/+Y this combat
        const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
        const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
        if (atk > 0 || hp > 0) {
          results.push({ type: 'buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
        }
        break;
      }

      case 'aura_buff_adjacent_health': {
        // Start of combat: Give adjacent allies +X/+Y (permanent)
        const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
        const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
        if (atk !== 0 || hp !== 0) {
          const left = sourceIndex - 1;
          const right = sourceIndex + 1;
          if (left >= 0 && board[left]) {
            results.push({ type: 'buff', targetIndex: left, atkDelta: atk, hpDelta: hp });
          }
          if (right < board.length && board[right]) {
            results.push({ type: 'buff', targetIndex: right, atkDelta: atk, hpDelta: hp });
          }
        }
        break;
      }

      case 'first_strike_passive': {
        // Passive: This unit always attacks first
        // This is handled by setting the firstStrike flag on the card itself
        // No runtime ability result needed - the flag is checked in combat
        break;
      }

      case 'buff_all_other_units': {
        // Summon: Give all other friendly units +X/+Y
        const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
        const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
        if (atk > 0 || hp > 0) {
          for (let i = 0; i < board.length; i++) {
            if (i !== sourceIndex && board[i]) {
              results.push({ type: 'buff', targetIndex: i, atkDelta: atk, hpDelta: hp });
            }
          }
        }
        break;
      }

      case 'buff_self_on_friendly_death': {
        // This ability is triggered by the on_friendly_death event
        // It buffs the unit itself when another friendly unit dies
        const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
        const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
        if (atk > 0 || hp > 0) {
          results.push({ type: 'buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
        }
        break;
      }

      case 'buff_all_on_buy': {
        // Triggered when you buy a unit: give ALL friendly units stats,
        // including this one (Arquitecto de Sonido).
        const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
        const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
        if (atk > 0 || hp > 0) {
          for (let i = 0; i < board.length; i++) {
            if (board[i]) {
              results.push({ type: 'buff', targetIndex: i, atkDelta: atk, hpDelta: hp });
            }
          }
        }
        break;
      }

      case 'swap_own_stats': {
        // Summon: Permanently swap this unit's Attack and Health
        // (El Aprendiz Hidráulico). Expressed as deltas so it composes
        // with the generic buff application path.
        const atkDelta = sourceCard.health - sourceCard.attack;
        const hpDelta = sourceCard.attack - sourceCard.health;
        if (atkDelta !== 0 || hpDelta !== 0) {
          results.push({ type: 'buff', targetIndex: sourceIndex, atkDelta, hpDelta });
          results.push(...swapReactions(board, sourceIndex));
        }
        break;
      }

      case 'add_random_card_to_hand': {
        // Summon: Add a random Tier X minion to your hand (Esquinero).
        const tier = (ability.params && ability.params.tier != null) ? ability.params.tier : 1;
        results.push({ type: 'add_card', tier });
        break;
      }

      case 'discover_unit': {
        // Summon: Discover a unit — three choices, keep one.
        // `tribe: "self"` means "your chosen tribe", i.e. whatever tribe the
        // summoner belongs to (Reclutador de Cuadra, Fan de Hueso Colorado).
        // `tier` defaults to the summoner's own tier; `tierOffset` shifts it
        // (Traficante de Mercado discovers from the tier below itself).
        const rawTribe = ability.params?.tribe;
        const tribe: TribeId | undefined =
          rawTribe === 'self' ? sourceCard.tribe : rawTribe;
        const offset = ability.params?.tierOffset ?? 0;
        const baseTier = ability.params?.tier ?? sourceCard.tier;
        const tier = Math.max(1, baseTier + offset);
        results.push({ type: 'discover', tier, tribe });
        break;
      }

      case 'buff_self_on_buy': {
        // Triggered when you buy a unit (for units like Rookie Riffer)
        const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
        const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
        if (atk > 0 || hp > 0) {
          results.push({ type: 'buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
        }
        break;
      }

      case 'aura_buff_other_units_combat': {
        // Aura: Buffs all other units at start of combat (Practice Amp, City Guardian)
        const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
        const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
        if (atk > 0 || hp > 0) {
          for (let i = 0; i < board.length; i++) {
            if (i !== sourceIndex && board[i]) {
              results.push({ type: 'buff', targetIndex: i, atkDelta: atk, hpDelta: hp });
            }
          }
        }
        break;
      }

      case 'buff_self_after_combat': {
        // After combat: Gain permanent stats (Steady-Hand Apprentice, Ink Apprentice)
        const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
        const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
        if (atk > 0 || hp > 0) {
          results.push({ type: 'buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
        }
        break;
      }

      case 'buff_random_shop': {
        // Buffs a random unit in the shop (Record Collector, Hype Intern)
        const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
        const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
        if (atk > 0 || hp > 0) {
          results.push({ type: 'buff_shop', atkDelta: atk, hpDelta: hp });
        }
        break;
      }

      case 'buff_self_on_first_damage': {
        // On first damage taken: Gain stats (Corner Guard)
        // Only trigger if this is the first time damaged
        if (context.isFirstTime) {
          const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
          const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
          if (atk > 0 || hp > 0) {
            results.push({ type: 'buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
          }
        }
        break;
      }

      case 'heal_self_after_combat': {
        // After combat: Heal this unit (Pan Master)
        const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
        if (hp > 0) {
          results.push({ type: 'buff', targetIndex: sourceIndex, atkDelta: 0, hpDelta: hp });
        }
        break;
      }

      case 'buff_self_on_damage': {
        // On any damage taken: Gain stats (Urban Colossus)
        // Triggers on any damage, not just first time
        const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
        const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
        if (atk > 0 || hp > 0) {
          results.push({ type: 'buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
        }
        break;
      }

      case 'buff_self_if_only_unit': {
        // Start of combat: If this is the only unit, gain stats (Lone Survivor)
        if (board.length === 1) {
          const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
          const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
          if (atk > 0 || hp > 0) {
            results.push({ type: 'combat_buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
          }
        }
        break;
      }

      case 'cash_in_plaque': {
        // Start of combat: Gain +attackPer/+healthPer for each Plaque tag on this unit.
        // Snowball engine T6 finisher mechanic — reads sourceCard.plaqueStacks.
        // Buff is combat-only (uses combat_buff result) so the tag count is preserved
        // for future combats; the stack count is the asset, the stat boost is the payoff.
        const stacks = sourceCard.plaqueStacks ?? 0;
        if (stacks > 0) {
          const atkPer = (ability.params && ability.params.attackPer != null) ? ability.params.attackPer : 0;
          const hpPer = (ability.params && ability.params.healthPer != null) ? ability.params.healthPer : 0;
          const atk = atkPer * stacks;
          const hp = hpPer * stacks;
          if (atk > 0 || hp > 0) {
            results.push({ type: 'combat_buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
          }
        }
        break;
      }

      case 'buff_self_if_attack_greater_health': {
        // Start of combat: If attack > health, gain stats (Blade Dancer)
        if (sourceCard.attack > sourceCard.health) {
          const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
          const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
          if (atk > 0 || hp > 0) {
            results.push({ type: 'combat_buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
          }
        }
        break;
      }

      case 'heal_adjacent_start_combat': {
        // Start of combat: Heal adjacent units (City Medic)
        const healAmount = (ability.params && ability.params.health != null) ? ability.params.health : 0;
        if (healAmount > 0) {
          // Heal left adjacent
          if (sourceIndex > 0 && board[sourceIndex - 1]) {
            results.push({ type: 'buff', targetIndex: sourceIndex - 1, atkDelta: 0, hpDelta: healAmount });
          }
          // Heal right adjacent
          if (sourceIndex < board.length - 1 && board[sourceIndex + 1]) {
            results.push({ type: 'buff', targetIndex: sourceIndex + 1, atkDelta: 0, hpDelta: healAmount });
          }
        }
        break;
      }

      case 'buff_self_if_leftmost': {
        // Start of combat: If this is the leftmost unit, gain stats (Little Homie)
        if (sourceIndex === 0) {
          const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
          const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
          if (atk > 0 || hp > 0) {
            results.push({ type: 'combat_buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
          }
        }
        break;
      }

      case 'swap_attack_health_combat': {
        // Start of combat: Swap this unit's Attack and Health (Fade Specialist)
        const currentAtk = sourceCard.attack;
        const currentHp = sourceCard.health;
        // We'll swap by buffing/debuffing to achieve the swap
        const atkDelta = currentHp - currentAtk;
        const hpDelta = currentAtk - currentHp;
        results.push({ type: 'combat_buff', targetIndex: sourceIndex, atkDelta: atkDelta, hpDelta: hpDelta });
        results.push(...swapReactions(board, sourceIndex));
        break;
      }

      case 'buff_tribal_units': {
        // Summon: Give all friendly units of a specific faction/tribe permanent stats (Barrio Patriarch)
        const faction = ability.params ? ability.params.faction : undefined;
        const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
        const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
        if ((atk > 0 || hp > 0) && faction) {
          for (let i = 0; i < board.length; i++) {
            const unit = board[i];
            if (unit && unit.faction === faction && i !== sourceIndex) {
              results.push({ type: 'buff', targetIndex: i, atkDelta: atk, hpDelta: hp });
            }
          }
        }
        break;
      }

      // --- Avenge abilities ---
      case 'avenge_buff_self': {
        // Avenge: Gain +X/+Y after X friendly units die
        const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
        const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
        if (atk > 0 || hp > 0) {
          results.push({ type: 'buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
        }
        break;
      }

      case 'avenge_buff_random': {
        // Avenge: Give a random friendly unit +X/+Y
        let validTargets = board
          .map((card, index) => ({ card, index }))
          .filter(item => item.card && item.card.health > 0);

        if (validTargets.length > 0) {
          const target = validTargets[Math.floor(Math.random() * validTargets.length)];
          const atk = (ability.params && ability.params.attack != null) ? ability.params.attack : 0;
          const hp = (ability.params && ability.params.health != null) ? ability.params.health : 0;
          if (atk > 0 || hp > 0) {
            results.push({ type: 'buff', targetIndex: target.index, atkDelta: atk, hpDelta: hp });
          }
        }
        break;
      }

      case 'avenge_summon': {
        // Avenge: Summon a token
        const tokenId = ability.params ? ability.params.tokenId : undefined;
        if (tokenId) {
          results.push({ type: 'summon', position: sourceIndex, cardId: tokenId });
        }
        break;
      }

      // === Power-fantasy payoffs ===
      // These back the tribe verbs on T4–T6 carries. Each one is meant to be
      // a visible swing on the round it lands, not a rounding error.

      case 'set_attack_to_health': {
        // BRINCO: the hop. Attack copies Health — the "2/40 wall becomes a
        // 40/40 monster" move. Never downgrades an already-larger Attack.
        if (sourceCard.health > sourceCard.attack) {
          results.push({
            type: 'combat_buff',
            targetIndex: sourceIndex,
            atkDelta: sourceCard.health - sourceCard.attack,
            hpDelta: 0,
          });
        }
        break;
      }

      case 'swap_all_if_beneficial': {
        // Padrino del Bulevar: swap Atk/HP for every friendly where it's a
        // net gain in Attack. Turns a board of walls into a board of threats.
        for (let i = 0; i < board.length; i++) {
          const unit = board[i];
          if (!unit) continue;
          if (unit.health > unit.attack) {
            results.push({
              type: 'combat_buff',
              targetIndex: i,
              atkDelta: unit.health - unit.attack,
              hpDelta: unit.attack - unit.health,
            });
            results.push(...swapReactions(board, i));
          }
        }
        break;
      }

      case 'buff_tribe_combat': {
        // Give every friendly of a named tribe stats for this combat.
        const tribe = ability.params?.tribe;
        const atk = ability.params?.attack ?? 0;
        const hp = ability.params?.health ?? 0;
        if (tribe && (atk !== 0 || hp !== 0)) {
          for (let i = 0; i < board.length; i++) {
            if (board[i]?.tribe === tribe) {
              results.push({ type: 'combat_buff', targetIndex: i, atkDelta: atk, hpDelta: hp });
            }
          }
        }
        break;
      }

      case 'buff_tribe_lower_stat': {
        // Mero Mero del Cruise: shore up whichever stat is lagging on each
        // tribe member, so the hop that follows has more to work with.
        const tribe = ability.params?.tribe;
        const amount = ability.params?.amount ?? 0;
        if (tribe && amount > 0) {
          for (let i = 0; i < board.length; i++) {
            const unit = board[i];
            if (!unit || unit.tribe !== tribe) continue;
            const toAttack = unit.attack <= unit.health;
            results.push({
              type: 'combat_buff',
              targetIndex: i,
              atkDelta: toAttack ? amount : 0,
              hpDelta: toAttack ? 0 : amount,
            });
          }
        }
        break;
      }

      case 'buff_self_per_tribe': {
        // Leyenda del Pit: scales with how deep you've committed to the tribe.
        const tribe = ability.params?.tribe;
        const atkPer = ability.params?.attackPer ?? 0;
        const hpPer = ability.params?.healthPer ?? 0;
        if (tribe) {
          const count = board.filter((u, i) => u && u.tribe === tribe && i !== sourceIndex).length;
          if (count > 0 && (atkPer > 0 || hpPer > 0)) {
            results.push({
              type: 'combat_buff',
              targetIndex: sourceIndex,
              atkDelta: atkPer * count,
              hpDelta: hpPer * count,
            });
          }
        }
        break;
      }

      case 'cash_in_plaque_board': {
        // Mito del Barrio: reads EVERY friendly's Plaque tags, not just its
        // own. The reward for a board that has been tagging all game.
        const totalStacks = board.reduce((sum, u) => sum + (u?.plaqueStacks ?? 0), 0);
        const atkPer = ability.params?.attackPer ?? 0;
        const hpPer = ability.params?.healthPer ?? 0;
        if (totalStacks > 0 && (atkPer > 0 || hpPer > 0)) {
          results.push({
            type: 'combat_buff',
            targetIndex: sourceIndex,
            atkDelta: atkPer * totalStacks,
            hpDelta: hpPer * totalStacks,
          });
        }
        break;
      }

      case 'grant_self_flags': {
        // Grants keywords to the source unit (Estelar Mayor: First Strike +
        // Cleave). Applied directly — flags aren't expressible as stat deltas.
        const flags: string[] = ability.params?.flags ?? [];
        if (flags.length > 0) {
          if (!sourceCard.flags) sourceCard.flags = {};
          for (const flag of flags) {
            (sourceCard.flags as Record<string, boolean>)[flag] = true;
          }
          results.push({
            type: 'log',
            message: `${sourceCard.name} gained ${flags.join(', ')}.`,
          });
        }
        break;
      }

      case 'grant_all_flags': {
        // Don Productor: hands a keyword to the whole board.
        const flags: string[] = ability.params?.flags ?? [];
        const includeSelf = ability.params?.includeSelf !== false;
        if (flags.length > 0) {
          for (let i = 0; i < board.length; i++) {
            const unit = board[i];
            if (!unit) continue;
            if (!includeSelf && i === sourceIndex) continue;
            if (!unit.flags) unit.flags = {};
            for (const flag of flags) {
              (unit.flags as Record<string, boolean>)[flag] = true;
            }
          }
          results.push({
            type: 'log',
            message: `${sourceCard.name} granted ${flags.join(', ')} to the board.`,
          });
        }
        break;
      }

      case 'summon_multiple_tokens': {
        // Mero Mero de la Clica: arrives with the crew already in tow.
        const tokenId = ability.params?.tokenId;
        const count = ability.params?.count ?? 1;
        if (tokenId) {
          for (let i = 0; i < count; i++) {
            results.push({ type: 'summon', position: sourceIndex + 1, cardId: tokenId });
          }
        }
        break;
      }

      case 'plaque_tag_highest': {
        // PLACA: put the work into whoever is already the biggest threat.
        // Targets the highest-Attack Plaquero, falling back to the highest-
        // Attack friendly if the board has no other tribe members yet.
        const count = ability.params?.count ?? 1;
        if (count > 0) {
          const candidates = board
            .map((card, index) => ({ card, index }))
            .filter(item => item.card);
          const tribal = candidates.filter(item => item.card.tribe === 'plaqueros');
          const pool = tribal.length > 0 ? tribal : candidates;
          if (pool.length > 0) {
            const best = pool.reduce(
              (a, b) => (b.card.attack > a.card.attack ? b : a),
              pool[0],
            );
            results.push({ type: 'plaque_tag', targetIndex: best.index, count });
          }
        }
        break;
      }

      // === Enemy-facing effects ===

      case 'debuff_enemy_board': {
        // El Surfer: the whole other side gets weaker for this combat.
        const atk = ability.params?.attack ?? 0;
        const hp = ability.params?.health ?? 0;
        if (atk !== 0 || hp !== 0) {
          results.push({ type: 'enemy_debuff', atkDelta: atk, hpDelta: hp });
        }
        break;
      }

      case 'buff_self_if_enemy_frontliner_weaker': {
        // Guardia Alerta: sizes up whoever is standing at the front of the
        // other board. No enemy board (tavern) means nothing to size up.
        const enemyFront = context.enemyBoard?.[0];
        if (enemyFront && enemyFront.health < sourceCard.health) {
          const atk = ability.params?.attack ?? 0;
          const hp = ability.params?.health ?? 0;
          if (atk > 0 || hp > 0) {
            results.push({ type: 'combat_buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
          }
        }
        break;
      }

      case 'damage_behind_target': {
        // Botas de Casquillo: the stomp carries through to the next enemy
        // in line behind the one just hit. Self-scoped — see the guard note
        // on buff_self_on_first_attack.
        if (context.attackerIndex !== sourceIndex) break;
        const amount = ability.params?.amount ?? 0;
        const hitIndex = context.enemyTargetIndex;
        if (amount > 0 && typeof hitIndex === 'number') {
          const behind = hitIndex + 1;
          if (context.enemyBoard && behind < context.enemyBoard.length) {
            results.push({ type: 'damage_enemy', targetIndex: behind, amount });
          }
        }
        break;
      }

      case 'drain_opponent_gold': {
        const amount = ability.params?.amount ?? 0;
        if (amount > 0) results.push({ type: 'drain_opponent_gold', amount });
        break;
      }

      // === Board manipulation ===

      case 'swap_with_right': {
        // Bailarín de Respaldo trades places with the unit behind it.
        const right = sourceIndex + 1;
        if (right < board.length && board[right]) {
          results.push({ type: 'swap_positions', a: sourceIndex, b: right });
        }
        break;
      }

      case 'swap_with_left': {
        // Vigía de la Cuadra steps forward past the ally in front of it.
        const left = sourceIndex - 1;
        if (left >= 0 && board[left]) {
          results.push({ type: 'swap_positions', a: sourceIndex, b: left });
        }
        break;
      }

      case 'copy_random_friendly_stats': {
        // La del Ring Light mirrors an ally's statline for the combat.
        const candidates = board
          .map((card, index) => ({ card, index }))
          .filter((item) => item.card && item.index !== sourceIndex);
        if (candidates.length > 0) {
          const pick = candidates[Math.floor(Math.random() * candidates.length)];
          results.push({
            type: 'combat_buff',
            targetIndex: sourceIndex,
            atkDelta: pick.card.attack - sourceCard.attack,
            hpDelta: pick.card.health - sourceCard.health,
          });
        }
        break;
      }

      // === Conditional self-buffs ===

      case 'buff_self_if_tribe_variety': {
        // Explorador Urbano rewards a deliberately splashed board.
        const required = ability.params?.count ?? 3;
        const tribes = new Set(board.filter((u) => u?.tribe).map((u) => u!.tribe));
        if (tribes.size >= required) {
          const atk = ability.params?.attack ?? 0;
          const hp = ability.params?.health ?? 0;
          if (atk > 0 || hp > 0) {
            results.push({ type: 'combat_buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
          }
        }
        break;
      }

      case 'buff_self_if_shop_has_tribe': {
        // Parroquiano del Foro scans the shop for its own tribe. Resolved
        // in the tavern (where the shop exists) as a combat-only buff, so
        // it reads as a start-of-combat bonus without needing shop state
        // plumbed all the way into the combat sim.
        const tribe = ability.params?.tribe === 'self' ? sourceCard.tribe : ability.params?.tribe;
        const shop = context.shop ?? [];
        if (tribe && shop.some((c) => c && c.tribe === tribe)) {
          const atk = ability.params?.attack ?? 0;
          const hp = ability.params?.health ?? 0;
          if (atk > 0 || hp > 0) {
            results.push({ type: 'combat_buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
          }
        }
        break;
      }

      case 'buff_self_if_hero_buffs_units': {
        // Estudiante Novata leans on a stat-granting hero power.
        if (context.heroPowerBuffsUnits) {
          const atk = ability.params?.attack ?? 0;
          const hp = ability.params?.health ?? 0;
          if (atk > 0 || hp > 0) {
            results.push({ type: 'buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
          }
        }
        break;
      }

      case 'buff_self_if_bought_enough': {
        // Trotamundos Veterano pays off a busy shopping turn. The buy count
        // rides in on damageAmount-style context supplied by the store.
        const required = ability.params?.count ?? 3;
        const bought = context.buysThisTurn ?? 0;
        if (bought >= required) {
          const atk = ability.params?.attack ?? 0;
          const hp = ability.params?.health ?? 0;
          if (atk > 0 || hp > 0) {
            results.push({ type: 'buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
          }
        }
        break;
      }

      case 'buff_self_every_nth_turn': {
        // Volantero: every second turn, not every turn.
        const period = ability.params?.period ?? 2;
        const turn = context.roundNumber ?? 0;
        if (period > 0 && turn > 0 && turn % period === 0) {
          const atk = ability.params?.attack ?? 0;
          const hp = ability.params?.health ?? 0;
          if (atk > 0 || hp > 0) {
            results.push({ type: 'buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
          }
        }
        break;
      }

      case 'buff_self_if_played_enough': {
        // Organizador de Fiesta counts units played this turn.
        const required = ability.params?.count ?? 3;
        const played = context.playsThisTurn ?? 0;
        if (played >= required) {
          const atk = ability.params?.attack ?? 0;
          const hp = ability.params?.health ?? 0;
          if (atk > 0 || hp > 0) {
            results.push({ type: 'buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
          }
        }
        break;
      }

      // === Attack-driven scaling ===

      case 'buff_self_on_deal_damage': {
        // Aprendiz de Placazos banks a little every time it connects.
        const atk = ability.params?.attack ?? 0;
        const hp = ability.params?.health ?? 0;
        if (atk > 0 || hp > 0) {
          results.push({ type: 'buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
        }
        break;
      }

      case 'buff_self_on_first_attack': {
        // Veterano del Círculo: only if THIS unit is the one that swung.
        // on_first_attack fires on every slot, so the guard is what makes
        // this self-scoped rather than a board-wide handout.
        if (context.attackerIndex !== sourceIndex) break;
        const atk = ability.params?.attack ?? 0;
        const hp = ability.params?.health ?? 0;
        if (atk > 0 || hp > 0) {
          results.push({ type: 'buff', targetIndex: sourceIndex, atkDelta: atk, hpDelta: hp });
        }
        break;
      }

      case 'buff_ally_on_first_attack': {
        // Fotógrafa del Flash rewards whoever swung first, not herself.
        const atk = ability.params?.attack ?? 0;
        const hp = ability.params?.health ?? 0;
        const attackerIndex = context.attackerIndex;
        if (
          (atk > 0 || hp > 0) &&
          typeof attackerIndex === 'number' &&
          attackerIndex !== sourceIndex &&
          board[attackerIndex]
        ) {
          results.push({ type: 'buff', targetIndex: attackerIndex, atkDelta: atk, hpDelta: hp });
        }
        break;
      }

      // === Shop-facing tavern effects ===

      case 'grant_free_refresh': {
        // Reported as a log line; the store reads the ability directly when
        // it tallies free-refresh credits (see match store `refreshCredits`).
        break;
      }

      case 'amplify_permanent_buffs':
      case 'bonus_on_gain_stats':
      case 'watch_friendly_gain_stats':
      case 'counts_as_two_for_tribe':
      case 'reduce_damage_taken':
      case 'reduce_damage_behind':
      case 'bonus_on_stats_swapped': {
        // Passive markers. Read directly by buffs.ts / tribeSystem.ts /
        // combatEngine rather than dispatched here, so there is nothing to
        // resolve — but they must not fall through to the "unimplemented"
        // log below.
        break;
      }

      case 'damage_random_enemies': {
        // Francotirador. Resolved by the combat engine via the tribe/opening
        // damage path; in the tavern it is a no-op by design.
        break;
      }

      case 'guard': {
        // Marker ability. Guard targeting is driven by the 'guard' tag in
        // combatEngine, so there is nothing to resolve here.
        break;
      }

      default:
        // Log any abilities that are defined on cards but not yet implemented here.
        results.push({
          type: "log",
          message: `Ability '${ability.id}' triggered but is not implemented in the engine.`,
        });
        break;
    }
  }

  return results;
}
